import { ServerListResponse } from '@lumapps/base-api';
import { UseQueryOptions } from '@lumapps/base-api/react-query';
import { ChatProviderId } from '@lumapps/chat/types';
import { CustomizationComponentProps } from '@lumapps/customizations/types';
import { Group } from '@lumapps/groups/types';
import { AvatarProps, ListItemProps, Theme, UserBlockSize } from '@lumapps/lumx/react';
import { Route } from '@lumapps/router';
import { TranslatableObject } from '@lumapps/translations/types';
import type { UseSocialProfileRouteProps } from '@lumapps/user-profile/hooks/useSocialProfileRoute';
import { ObjectValues } from '@lumapps/utils/types/ObjectValues';
import { PartialExcept } from '@lumapps/utils/types/PartialExcept';

import { DEFAULT_API_PROFILE_FIELD_PATTERNS } from './constants';

export enum UserAccountType {
    EXTERNAL = 'external',
    GOOGLE = 'google',
    MICROSOFT = 'microsoft',
    OKTA = 'okta',
}

export enum LoginProvider {
    GOOGLE = 'google',
    MICROSOFT = 'microsoft',
    // email is for external users
    EMAIL = 'email',
}

export interface Subscription {
    feed: string;
    uid?: string;
    level?: string;
    status?: string;
}

/** Additional informations on the user */
export interface ApiProfile {
    /** user's profile picture url, please use `getUserProfilePictureUrl` for callbacks */
    profilePicture?: string;
    /** user's profile picture url, please use `getUserProfilePictureUrl` for callbacks */
    thumbnailPhotoUrl?: string;
    /** user's profile picture object, please use `getUserProfilePictureUrl` for callbacks */
    thumbnail?: {
        /** The data of the profile picture. */
        photoData?: string;
        /** The mimeType of the profile picture. */
        mimeType?: string;
        etag?: string;
    };
    organizations?: {
        department: string;
        title: string;
        customType?: string;
        primary: boolean;
        location: string;
    }[];
    /** Whether the author is admin of the current community. */
    isAdmin?: boolean;
    /** Additional informations on the user name. */
    name?: {
        familyName?: string;
        fullName?: string;
        givenName?: string;
    };
    /** other details TBD when needed */
    [key: string]: any;
}

/** adding both upper and lower case constants for retrocompatibility purposes */
export const USER_STATUS_ENABLED = ['ENABLED', 'enabled', 'LIVE', 'live'] as const;
export const USER_STATUS_INACTIVE = ['DISABLED', 'disabled', 'DELETED', 'deleted'] as const;
export type Status = (typeof USER_STATUS_ENABLED)[number] | (typeof USER_STATUS_INACTIVE)[number];

export interface User {
    /** user's id */
    id: string;
    uid?: string;
    userId?: string;
    /**
     * @deprecated please use `getUserFullName` function instead from `useUser` hook
     */
    fullName: string;
    /** user account type (eg. Google, Microsoft...) */
    accountType?: UserAccountType;
    /** user's last name */
    lastName: string;
    /** user's first name */
    firstName: string;
    /** user's primary email */
    email: string;
    password?: string;
    rePassword?: string;
    /** user's job title */
    jobTitle?: string;
    location?: string;
    updatedAt?: string;
    createdAt?: string;
    /** user's profile picture url, please use `getUserProfilePictureUrl` for callbacks */
    profilePictureUrl?: string;
    pictureUrl?: string;
    /** user's profile picture url, please use `getUserProfilePictureUrl` for callbacks */
    profilePicture?: string;
    /** The customer id. */
    customer?: string;
    customerId?: string;
    /** user identification token */
    token?: string;
    /** languages for the current user */
    langs?: string[];
    lang?: string;
    /** user's details */
    apiProfile?: ApiProfile;
    customProfile?: any;
    isGuest?: boolean;
    subscriptions?: Subscription[];
    /** whether the user is already followed */
    isFollowed?: boolean;
    /** url of the user's badge picture */
    badgePictureUrl?: string;
    /** name of the department that the user is part of */
    departmentName?: string;
    department?: string;
    /** The user primary field values */
    profilePrimaryFieldValues?: string[];
    /**  user's location */
    locationName?: string;
    /** id of the organization that the user is part of */
    organizationId?: string;
    /** User settings and preferences */
    settings?: {
        hasAcceptedCookies?: boolean;
        hasAcceptedTerms?: boolean;
        cookieBannerReadAt?: string;
        notifications?: any;
        notificationPreferences?: any;
        social?: {
            isActivityVisible?: boolean;
        };
    };
    /** Whether the user has a god-like status. */
    isGod?: boolean;
    /** Whether the user has flagged as a designer or not */
    isDesigner?: boolean;
    /** Whether the user is a platform admin. */
    isSuperAdmin?: boolean;
    /** whether the user is hidden on the search and user directory */
    isHidden?: boolean;
    /** The list of instances for which the user is admin. */
    instancesSuperAdmin?: string[];
    /** Whether the user is connected using the `as` feature. */
    isConnectedAs?: boolean;
    /** The login service provider of the user */
    loginProvider?: LoginProvider;
    /** status of whether the user is enabeld or not */
    status?: Status;
    /**
     * list of tutorials "seen" timestamps grouped by instance id.
     * When we are updating the tutorials, this becomes another entirely different object
     * #blameTheBackend.
     */
    tutorials?:
        | Record<string, Record<string, string>>
        | {
              instance: string;
              tutorialId: string;
          };
}

/** The common representation of a user.
 * It only contains the strictly mimimum that should be
 * available to display an user in most cases.
 * @see https://lumapps-api.stoplight.io/docs/lumapps-private-api/6ac885835cb84-user-reference
 */
export interface UserReference {
    /** The user's id.
     * @example '657399200472902'
     */
    id: string;
    /** The user's primary email.
     * @example hermannm@voigt.de
     */
    email: string;
    /** The user's full name.
     * @example Hermann Münch
     */
    fullName: string;
    /** The user's first name.
     * @example Hermann
     */
    firstName?: string;
    /** The user's last name.
     * @example Münch
     */
    lastName?: string;
    /** Job title of the user.
     * @example Chemical technician
     */
    jobTitle?: string;
    /** Location of the user.
     * @example Paris, France
     */
    locationName?: string;
    /** Department of the user.
     * @example Human ressources
     */
    departmentName?: string;
}

export type MinimumViableUser<K extends keyof User = never> = PartialExcept<User, K>;

export interface UserSlice {
    /** Currently connected user */
    connectedUser?: User;
    /**
     * `initialized` is here to avoid mapReduxToAngular to be triggered when another action is
     * dispatch while the user has not been set yet by the angular state,
     * the first __angular__/user/update will set this to `true`.
     */
    initialized: boolean;
    /** List of user by id */
    entities: Record<string, User>;
    userList: {
        isLoading: boolean;
        cursor?: string;
        ids: any[];
        hasMore: boolean;
    };
    blobAvatars: Record<User['id'], string>;
}

export interface UserListSuccessPayload extends Partial<ServerListResponse<User>> {
    items: User[];
    more: boolean;
    add?: boolean;
}

export interface UserListRequestParams {
    more?: boolean;
    searchTerm?: string;
    queryFields?: string[];
    retrieveAllFields?: boolean;
    add?: boolean;
    cursor?: string;
    instance?: string;
    maxResults?: number;
    showHidden?: boolean;
    lang?: string;
    fields?: string;
    reactedEntityKey?: string;
    reactedEntityKind?: string;
    query?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    feeds?: string | string[];
    types?: string[];
    status?: Status | Status[];
    sortOrder?: string | null;
    groups?: Group[];
    /** used to search specifics user ids */
    ids?: string[];
}

/** The different types profile fields can have. */
export enum ProfileFieldType {
    text = 'text',
    user = 'user',
    metadata = 'metadata',
    float = 'float',
    date = 'date',
}

/**
 * Base user field interface that contains common properties for all profile fields.
 * This interface is used to define the structure of user profile fields.
 */
export interface BaseUserField {
    definition_id?: string;
    /** id of the profile field */
    id: string;
    /** class of the icon to be used for this profile field */
    iconClass?: string;
    /** value of the field as a string */
    value?: string;
    /** value of the field as an array of string, if value is multiple */
    values?: (string | undefined)[];
    /** user profile field */
    type?: string;
    /** user field name */
    name?: string;
}

/** Fields shared between all profile field types. */
export interface CommonProfileField extends Omit<BaseUserField, 'value' | 'values'> {
    /** whether the contact field is editable or not */
    editable: boolean;
    /** url of the icon to be used for this profile field */
    iconUrl: string;
    /** id of the organization the user is part of */
    organizationId: string;
    /** whether one can use this field as a search criteria */
    searchable: boolean;
    /** title of this profile field */
    title: TranslatableObject;
    /** The user linked to the profile field. */
    user?: User | null;
    /** this field's user's id */
    userId: string;
    /** value of the field as a translatable object */
    value?: TranslatableObject;
    /** value of the field as an array of translatable objects, if value is multiple */
    values?: Array<TranslatableObject>;
    /** The order to display the field in. */
    order?: number;
    /** Determines if the field should span the full width of its container in the UI. */
    isFullWidth?: boolean;
}

/**
 * The "User" type profile field specific fields.
 */
export interface UserProfileField extends CommonProfileField {
    type: ProfileFieldType.user;
    user: User;
}

/**
 * The "text" types profile field specific fields.
 */
export interface TextProfileField extends CommonProfileField {
    type: ProfileFieldType.text;
    user?: null;
}

/**
 * The "metadata" types profile field specific fields.
 */
export interface MetadataProfileField extends CommonProfileField {
    type: ProfileFieldType.metadata;
    user?: null;
}

/** The different type contact fields can have. */
export enum ContactFieldType {
    email = 'email',
    phone = 'phone',
    mobilePhone = 'mobile_phone',
    chat = 'chat',
}

export interface CommonContactFields extends CommonProfileField {
    contactId: string;
    /** whether this field is the preferred contact method or not */
    preferredContact: boolean;
    /** link to the user using this contact field e.g. slack url or phone number */
    url: string;
    /** deep link to the contact application, if available */
    nativeUrl?: string;
}
export interface DefaultContactField {
    /** The field type. */
    type: ContactFieldType | string;
    providerType: undefined;
}

/** Chat contact fields have an additional provider */
export interface ChatContactField {
    type: ContactFieldType.chat;
    providerType: ChatProviderId;
}

export type ContactField = CommonContactFields &
    (
        | {
              /** The field type. */
              type: ContactFieldType | string;
              providerType: undefined;
          }
        | {
              type: ContactFieldType.chat;
              providerType: ChatProviderId;
          }
    );

export type ProfileField = UserProfileField | TextProfileField;

export interface UserProfile extends User {
    /** url of the user's badge picture */
    coverPictureUrl: string;
    /** user's job title */
    profileFields?: Array<ProfileField>;
    /** user's job title */
    contactFields?: Array<ContactField>;
    /** whether the user can edit their profile */
    canEditProfile?: boolean;
    /**
     * The directory id used to retrieves the user data.
     * If for some reason the backend can't find a directoryId, the value will be null.
     * In that case, the user shouldn't be allowed to edit their profile.
     * */
    directoryId: string | null;
}

export type FieldValue = string | boolean | number | Record<string, any> | null;

export type Selector = {
    key: string;
    filterKey?: string;
    filterValue?: FieldValue;
};

export enum USER_MENU_LINK_TYPE {
    LINK = 'LINK',
    BUTTON = 'BUTTON',
    DIVIDER = 'DIVIDER',
    CUSTOMIZATION = 'CUSTOMIZATION',
}

export type UserMenuLink = {
    key: string;
    icon?: string;
    label?: string;
    url?: string;
    target?: string;
    type?: USER_MENU_LINK_TYPE;
    onClick?: (evt?: any) => void;
    route?: Route;
    isLoading?: boolean;
    tooltipLabel?: string;
    isDisabled?: boolean;
    customizationProps?: CustomizationComponentProps;
};

export type UserProfileListItemProps = ListItemProps & {
    /** The avatar of the user */
    avatarImageSrc?: string;
    /** The name */
    name?: string;
    /** The url to the userProfile */
    userProfileUrl?: string;
    /** Custom className */
    className?: string;
    /** The list item size */
    size?: ListItemProps['size'];
    /** The block size */
    userBlockSize?: UserBlockSize;
    /** The theme */
    theme?: Theme;
    /** Custom? style */
    style?: Record<string, string>;
    /** callback when the view user profile link is clicked */
    onClick?: () => void;
    /** scope where this component is used. This prop will be used for tracking purposes */
    scope: string;
};

export type AvailableApiProfileFieldPatterns = ObjectValues<typeof DEFAULT_API_PROFILE_FIELD_PATTERNS>;

export type UseUserFieldsOptions = {
    /** list of fields to display, possible values are */
    fields?: string[];
    /** additional fields to be displayed */
    additionalFields?: (string | React.ReactNode)[];
};

export type UseUserAvatarOptions = {
    /** override the avatar's URL */
    customUserAvatar?: string;
    /** size to which the avatar should be resized */
    resizeAvatarTo?: AvatarProps['size'];
    /** whether the avatar should be retrieved from the api profile rather than calculating it */
    useAvatarFromApiProfile?: boolean;
};

export type UseUserOptions = UseQueryOptions & UseUserFieldsOptions & UseUserAvatarOptions & UseSocialProfileRouteProps;

export interface BaseUserComponentProps extends UseUserOptions {
    /** Base user info */
    user?: MinimumViableUser;
    /** Class name to forward */
    className?: string;
    /** (default: true) Displays the user card popover on mouse hover. */
    showUserCardOnHover?: boolean;
    /** scope for tracking purposes */
    scope?: string;
    /** Image URL to use in case user's avatar is not available */
    fallbackAvatar?: string;
    /** delay in order to display the user card on hover */
    userCardOnHoverDelay?: number;
    /** function that can be used for customizing the user's full name display */
    renderUserFullName?: (fullName: string) => string;
}
